/**
 * localStorage-backed idle-session marks shared by every tab of the
 * session: the last interaction, the JWT deadline, the idle length the
 * API asked for and the last keepalive. All values are epoch
 * milliseconds (or ms for the timeout); a missing or unreadable value
 * reads as `null`, so callers never see NaN.
 */
import { IDLE_STORAGE_KEYS, LOGOUT_HASH_STORAGE_KEY } from './constants';

const readNumber = key => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null || raw === '') return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch (_) {
    return null;
  }
};

const writeNumber = (key, value) => {
  try {
    if (value === null || value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, String(value));
  } catch (_) {
    // localStorage unavailable (private mode, quota): the in-memory
    // timers still run for this tab.
  }
};

export const readLastInteractionAt = () => readNumber(IDLE_STORAGE_KEYS.lastInteractionAt);
export const writeLastInteractionAt = at => writeNumber(IDLE_STORAGE_KEYS.lastInteractionAt, at);

export const readTokenDeadlineAt = () => readNumber(IDLE_STORAGE_KEYS.tokenDeadlineAt);
export const writeTokenDeadlineAt = at => writeNumber(IDLE_STORAGE_KEYS.tokenDeadlineAt, at);

export const readIdleTimeoutMs = () => readNumber(IDLE_STORAGE_KEYS.idleTimeoutMs);
export const writeIdleTimeoutMs = ms => writeNumber(IDLE_STORAGE_KEYS.idleTimeoutMs, ms);

export const readLastKeepaliveAt = () => readNumber(IDLE_STORAGE_KEYS.lastKeepaliveAt);
export const writeLastKeepaliveAt = at => writeNumber(IDLE_STORAGE_KEYS.lastKeepaliveAt, at);

/** Drop every mark — on logout, so the next sign-in starts clean. */
export const clearIdleStore = () => {
  Object.values(IDLE_STORAGE_KEYS).forEach(key => writeNumber(key, null));
};

export const readLogoutHash = () => {
  try {
    return localStorage.getItem(LOGOUT_HASH_STORAGE_KEY) || null;
  } catch (_) {
    return null;
  }
};

export const writeLogoutHash = hash => {
  try {
    if (hash) localStorage.setItem(LOGOUT_HASH_STORAGE_KEY, hash);
    else localStorage.removeItem(LOGOUT_HASH_STORAGE_KEY);
  } catch (_) {
    // see writeNumber
  }
};
